declare global {
  interface Window {
    _zoomBaselineDPR?: number;
  }
}

class ZoomPrevention {
  constructor() {
    this.isZoomDetectionActive = true;
    this.zoomCheckInterval = null;
    this.lastKnownZoomPercentage = 100; // Changed from lastKnownZoom
    this.initialZoomWarningShown = false;
    this.runtimeZoomWarningShown = false;
    this.hasShownInitialWarning = false;
    this.lastKnownAspectRatio = window.innerWidth / window.innerHeight;

    // Baseline DPR captured at init; used to compute zoom reliably regardless of window size
    this.baselineDevicePixelRatio = window.devicePixelRatio || 1;
    // Expose baseline on window for helper function getCurrentZoom()
    window._zoomBaselineDPR = this.baselineDevicePixelRatio;

    // Cooldown timestamp to ignore zoom checks right after a window resize
    this.resizeCooldownUntil = 0;

    // Rapid tap detection - suppress warnings when user is actively tapping
    this.rapidTapSuppressUntil = 0;
    this.recentTaps = [];

    // Store instance globally for button access
    window._zoomPrevention = this;

    this.init();
  }

  init() {
    // Check for existing zoom immediately
    setTimeout(() => this.detectInitialZoom(), 100);

    // Set up prevention for new zoom attempts
    this.setupZoomPrevention();

    // Set up continuous monitoring for runtime zoom changes
    this.setupZoomDetection();
  }

  detectInitialZoom() {
    // Use DPR-based approach to avoid false positives at various window sizes
    const zoomPercentage = this.getCurrentZoomPercentage();

    console.log('Initial zoom detection:', {
      outerWidth: window.outerWidth,
      innerWidth: window.innerWidth,
      outerInnerRatio: window.outerWidth / window.innerWidth,
      screenRatio: screen.width / window.innerWidth,
      visualViewportScale: window.visualViewport?.scale,
      devicePixelRatio: window.devicePixelRatio,
      zoomPercentage: zoomPercentage + '%',
    });

    console.log(zoomPercentage);
    // More sensitive detection - 3% tolerance
    if (Math.abs(zoomPercentage - 100) > 3) {
      console.warn(`Pre-existing zoom detected: ${zoomPercentage}%`);
      this.showInitialZoomWarning(zoomPercentage);
    } else {
      console.log(
        `No significant zoom detected on initial load (${zoomPercentage}%)`
      );
    }

    // Store baseline for comparison
    this.lastKnownZoomPercentage = zoomPercentage;
  }

  getCurrentZoomPercentage() {
    // Compute zoom relative to the DPR at initialization time
    const currentDPR = window.devicePixelRatio || 1;
    const zoom = (currentDPR / this.baselineDevicePixelRatio) * 100;
    return Math.round(zoom);
  }

  setupZoomPrevention() {
    // Keyboard zoom prevention
    document.addEventListener(
      'keydown',
      (e) => {
        // Allow Ctrl+0/Cmd+0 to reset zoom (don't prevent this!)
        if (
          (e.ctrlKey || e.metaKey) &&
          (e.key === '0' || e.code === 'Digit0')
        ) {
          console.log('Zoom reset allowed (Ctrl/Cmd+0)');
          return; // Allow the browser to handle zoom reset
        }

        const zoomKeys = ['+', '-', '='];
        const isZoomShortcut =
          (e.ctrlKey || e.metaKey) &&
          (zoomKeys.includes(e.key) || zoomKeys.includes(e.code));

        if (isZoomShortcut) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Show warning when user tries to zoom
          this.showRuntimeZoomWarning('keyboard shortcut');
          return false;
        }
      },
      { capture: true, passive: false }
    );

    // Mouse wheel zoom prevention
    document.addEventListener(
      'wheel',
      (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Show warning when user tries to zoom
          this.showRuntimeZoomWarning('mouse wheel');
          return false;
        }
      },
      { capture: true, passive: false }
    );

    // Touch gesture prevention
    let touchStartDistance = 0;

    document.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 2) {
          touchStartDistance = this.getTouchDistance(e.touches);
          e.preventDefault();
        }
      },
      { passive: false }
    );

    document.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const currentDistance = this.getTouchDistance(e.touches);
          const scale = currentDistance / touchStartDistance;

          // If significant pinch detected
          if (Math.abs(scale - 1) > 0.1) {
            this.showRuntimeZoomWarning('pinch gesture');
          }
        }
      },
      { passive: false }
    );

    // Gesture events for Safari
    ['gesturestart', 'gesturechange', 'gestureend'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (eventName === 'gesturestart') {
            this.showRuntimeZoomWarning('trackpad gesture');
          }

          return false;
        },
        { passive: false }
      );
    });

    // Track rapid tapping to suppress zoom warnings during active engagement
    document.addEventListener(
      'touchend',
      (e) => {
        const now = Date.now();

        // Add this tap to recent taps
        this.recentTaps.push(now);

        // Clean up taps older than 1 second
        this.recentTaps = this.recentTaps.filter(
          (tapTime) => now - tapTime < 1000
        );

        // If 3+ taps in the last second, user is actively engaged - suppress warnings
        if (this.recentTaps.length >= 3) {
          // Suppress zoom warnings for 2 seconds after rapid tapping stops
          this.rapidTapSuppressUntil = now + 2000;
          console.log('Rapid tapping detected - suppressing zoom warnings');
        }
      },
      { passive: true }
    );
  }

  setupZoomDetection() {
    // Continuous monitoring for zoom changes
    this.zoomCheckInterval = setInterval(() => {
      this.checkForZoomChange();
    }, 500);

    // Visual viewport monitoring (most reliable for detecting zoom changes)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        setTimeout(() => this.checkForZoomChange(), 100);
      });
    }

    // Window resize monitoring (backup)
    window.addEventListener('resize', () => {
      // Start a short cooldown to avoid misclassifying resizes as zoom
      this.resizeCooldownUntil = Date.now() + 800; // 800ms debounce window
      setTimeout(() => this.checkForZoomChange(), 200);
    });

    // Check when window regains focus (catches zoom changes in other tabs)
    window.addEventListener('focus', () => {
      setTimeout(() => this.checkForZoomChange(), 300);
    });
  }

  checkForZoomChange() {
    if (!this.isZoomDetectionActive) return;

    // Ignore checks during the resize cooldown window
    if (Date.now() < this.resizeCooldownUntil) {
      return;
    }

    // Calculate current aspect ratio
    const currentAspectRatio = window.innerWidth / window.innerHeight;

    // If aspect ratio change detected, treat as resize and reset baseline measurements
    if (Math.abs(this.lastKnownAspectRatio - currentAspectRatio) > 0.005) {
      // Update aspect ratio baseline
      this.lastKnownAspectRatio = currentAspectRatio;
      // Also refresh zoom baseline to current DPR so post-resize checks don't misfire
      this.baselineDevicePixelRatio =
        window.devicePixelRatio || this.baselineDevicePixelRatio;
      // And sync last known zoom to current so there is no sudden diff
      this.lastKnownZoomPercentage = this.getCurrentZoomPercentage();
      return;
    }

    const currentZoomPercentage = this.getCurrentZoomPercentage();
    const zoomDifference = Math.abs(
      currentZoomPercentage - this.lastKnownZoomPercentage
    );

    // If zoom returned to 100%, hide any existing modals
    if (Math.abs(currentZoomPercentage - 100) <= 3) {
      this.hideAllZoomModals();
      this.lastKnownZoomPercentage = currentZoomPercentage;
      return;
    }

    // If significant zoom change detected (more than 3%)
    if (zoomDifference > 3) {
      // Don't show warning if changing TO 100%
      if (Math.abs(currentZoomPercentage - 100) <= 3) {
        console.log('Zoom changed to 100% - problem solved');
        this.hideAllZoomModals();
      } else {
        console.warn(
          `Runtime zoom change detected: ${this.lastKnownZoomPercentage}% → ${currentZoomPercentage}%`
        );

        if (!this.hasShownInitialWarning) {
          // This might be the initial load still settling
          this.showInitialZoomWarning(currentZoomPercentage);
        } else {
          // This is definitely a runtime change
          this.showRuntimeZoomWarning(
            'browser zoom change',
            currentZoomPercentage
          );
        }
      }

      this.lastKnownZoomPercentage = currentZoomPercentage;
    }
  }

  showInitialZoomWarning(zoomPercentage) {
    if (this.initialZoomWarningShown) return;

    this.initialZoomWarningShown = true;
    this.hasShownInitialWarning = true;

    console.log('Showing initial zoom warning');

    const modal = this.createModal('initial', zoomPercentage);
    document.body.appendChild(modal);
  }

  showRuntimeZoomWarning(method, zoomPercentage = null) {
    // Suppress warning if user is actively tapping
    if (Date.now() < this.rapidTapSuppressUntil) {
      console.log('Zoom warning suppressed due to rapid tapping activity');
      return;
    }

    if (this.runtimeZoomWarningShown) return;

    this.runtimeZoomWarningShown = true;

    const currentZoomPercentage =
      zoomPercentage || this.getCurrentZoomPercentage();
    console.log(
      `Showing runtime zoom warning (${method}):`,
      currentZoomPercentage + '%'
    );

    const modal = this.createModal('runtime', currentZoomPercentage, method);
    document.body.appendChild(modal);

    // Reset the flag after a delay to allow multiple warnings if needed
    setTimeout(() => {
      this.runtimeZoomWarningShown = false;
    }, 5000);
  }

  createModal(type, zoomPercentage, method = null) {
    const modal = document.createElement('div');
    modal.setAttribute('data-zoom-modal', 'true');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    `;

    const isInitial = type === 'initial';
    const title = isInitial ? '⚠️ Zoom Detected' : '⚠️ Zoom Blocked';
    const bgColor = isInitial ? '#5c6bc0' : '#66bb6a';

    const methodText = method ? ` (via ${method})` : '';
    const description = isInitial
      ? `Browser zoom: <strong>${zoomPercentage}%</strong>`
      : `Current zoom: <strong>${zoomPercentage}%</strong>`;

    const urgentNote = isInitial
      ? `
      <div style="background: #e8f5e9; color: #2e7d32; padding: 10px; border-radius: 5px; margin: 12px 0; font-size: 14px;">
        This app works best at 100% zoom
      </div>
    `
      : `
      <div style="background: #e3f2fd; color: #1565c0; padding: 10px; border-radius: 5px; margin: 12px 0; font-size: 14px;">
        Zoom prevented - app requires 100%
      </div>
    `;

    modal.innerHTML = `
      <div style="background: ${bgColor}; padding: 20px; border-radius: 8px; text-align: center; max-width: 360px; box-shadow: 0 4px 16px rgba(0,0,0,0.2);">
        <h2 style="margin-top: 0; margin-bottom: 10px; font-size: 20px;">${title}</h2>
        
        <p style="font-size: 14px; margin-bottom: 8px;">${description}</p>
        
        ${urgentNote}
        
        <div style="background: rgba(255,255,255,0.1); padding: 10px; border-radius: 6px; margin: 12px 0;">
          <p style="margin: 5px 0; font-size: 13px;"><strong>Quick fix:</strong></p>
          <p style="margin: 5px 0; font-size: 13px;">Press <strong>Ctrl+0</strong> (PC) or <strong>Cmd+0</strong> (Mac)</p>
        </div>
        
        <div style="margin-top: 15px;">
          ${
            isInitial
              ? `
            <button data-action="fixed" onclick="window._zoomPrevention && window._zoomPrevention.checkAndHideModal();" 
                    style="background: #4caf50; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin: 3px; font-size: 14px; font-weight: bold;">
              ✓ Fixed - Continue
            </button>
            <br>
            <button onclick="this.parentElement.parentElement.parentElement.remove();" 
                    style="background: #757575; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; margin: 3px; font-size: 12px;">
              Continue Anyway
            </button>
          `
              : `
            <button onclick="this.parentElement.parentElement.parentElement.remove();" 
                    style="background: #4caf50; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin: 3px; font-size: 14px;">
              ✓ OK
            </button>
          `
          }
        </div>
      </div>
    `;

    return modal;
  }

  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  hideAllZoomModals() {
    // Find and remove all zoom warning modals
    const modals = document.querySelectorAll('[data-zoom-modal]');
    modals.forEach((modal) => modal.remove());

    // Reset warning flags so they can be shown again if needed
    this.initialZoomWarningShown = false;
    this.runtimeZoomWarningShown = false;
  }

  checkAndHideModal() {
    const currentZoom = this.getCurrentZoomPercentage();
    if (Math.abs(currentZoom - 100) <= 3) {
      // Zoom is fixed, hide the modal
      this.hideAllZoomModals();
      return true;
    } else {
      // Zoom is not fixed, show the modal again
      this.showInitialZoomWarning(currentZoom);
      return false;
    }
  }

  destroy() {
    this.isZoomDetectionActive = false;
    if (this.zoomCheckInterval) {
      clearInterval(this.zoomCheckInterval);
    }
  }
}

// Initialize the zoom prevention system
export function initializeZoomPrevention() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ZoomPrevention();
    });
  } else {
    new ZoomPrevention();
  }
}

// Utility function to check current zoom level
export function getCurrentZoom() {
  // Expose a DPR-based zoom computation that is robust to window resizes
  const baseline = window._zoomBaselineDPR || window.devicePixelRatio || 1;
  const zoom = ((window.devicePixelRatio || 1) / baseline) * 100;
  return Math.round(zoom);
}
